import { defineStore } from 'pinia'
import { ref } from 'vue'

export type NotificationType = 'error' | 'success' | 'warn' | 'info'

export type NotificationAction = {
  label: string
  onClick: () => void
}

type NotificationInput = {
  message: string
  type: NotificationType | undefined
  detail?: string
  sticky?: boolean
  life?: number
  group?: string
  /** A button beside the message, such as Undo. Clicking it also dismisses the notification. */
  action?: NotificationAction
}

export type AppNotification = NotificationInput & { id: number }

// Notifications stay here until they are dismissed or their life runs out;
// `NotificationStack` renders them above the bottom bars.
export const useNotificationStore = defineStore('notification', () => {
  const notifications = ref<AppNotification[]>([])
  let lastId = 0

  const add = (payload: NotificationInput) => {
    lastId += 1
    notifications.value.push({ ...payload, id: lastId })
    return lastId
  }

  const addNotification = (message: string) => {
    add({ message, type: 'success' })
  }

  const addError = (message: string, sticky: boolean = true, options?: { group?: string; life?: number }) => {
    add({
      message,
      type: 'error',
      sticky,
      group: options?.group,
      life: options?.life
    })
  }

  const removeNotification = (index: number) => {
    notifications.value.splice(index, 1)
  }

  const dismiss = (id: number) => {
    notifications.value = notifications.value.filter((notification) => notification.id !== id)
  }

  const removeGroup = (group: string) => {
    notifications.value = notifications.value.filter((notification) => notification.group !== group)
  }

  const clear = () => {
    notifications.value = []
  }

  return {
    notifications,
    add,
    addNotification,
    addError,
    removeNotification,
    dismiss,
    removeGroup,
    clear
  }
})
